import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabs } from '../../contexts/TabsContext';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogContent, TextField, Box, Typography, List, ListItemButton, ListItemText,
  Chip, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import SettingsIcon from '@mui/icons-material/Settings';
import ArticleIcon from '@mui/icons-material/Article';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InsightsIcon from '@mui/icons-material/Insights';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import { useHotkey } from '../../hooks/useHotkeys';
import { strategyScanService } from '../../services/strategyScan.service';
import { setThemeMode } from '../../store/authSlice';
import { AppDispatch, RootState } from '../../store';

type CommandKind = 'page' | 'symbol' | 'action';

interface Command {
  id: string;
  kind: CommandKind;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  exchange?: string;
  run: () => void;
}

const PAGE_COMMANDS = (navigate: ReturnType<typeof useNavigate>): Command[] => [
  { id: 'p:dashboard',    kind: 'page', title: 'Dashboard',         icon: <DashboardIcon fontSize="small" />,         run: () => navigate('/dashboard') },
  { id: 'p:trades',       kind: 'page', title: 'Trades',            icon: <TrendingUpIcon fontSize="small" />,        run: () => navigate('/trades') },
  { id: 'p:options',      kind: 'page', title: 'Options (hub)',     icon: <TrendingUpIcon fontSize="small" />,        run: () => navigate('/options') },
  { id: 'p:analytics',    kind: 'page', title: 'Analytics',         icon: <InsightsIcon fontSize="small" />,          run: () => navigate('/analytics') },
  { id: 'p:alerts',       kind: 'page', title: 'Alerts',            icon: <NotificationsIcon fontSize="small" />,     run: () => navigate('/alerts') },
  { id: 'p:strategies',   kind: 'page', title: 'Strategies',        icon: <ShowChartIcon fontSize="small" />,         run: () => navigate('/strategies') },
  { id: 'p:options-at',   kind: 'page', title: 'Options AT',        icon: <ShowChartIcon fontSize="small" />,         run: () => navigate('/option-strategies') },
  { id: 'p:golden-signals', kind: 'page', title: 'Golden Signals',  icon: <BookmarksIcon fontSize="small" />,        run: () => navigate('/golden-signals') },
  { id: 'p:charts',       kind: 'page', title: 'Charts',            icon: <CandlestickChartIcon fontSize="small" />,  run: () => navigate('/charts') },
  { id: 'p:watchlist',    kind: 'page', title: 'Watchlist',         icon: <VisibilityIcon fontSize="small" />,        run: () => navigate('/watchlist') },
  { id: 'p:fno',          kind: 'page', title: 'FNO Dashboard',     icon: <CandlestickChartIcon fontSize="small" />,  run: () => navigate('/options-dashboard') },
  { id: 'p:instruments',  kind: 'page', title: 'Instruments',       icon: <ShowChartIcon fontSize="small" />,         run: () => navigate('/instruments') },
  { id: 'p:logs',         kind: 'page', title: 'Logs',              icon: <ArticleIcon fontSize="small" />,           run: () => navigate('/logs') },
  { id: 'p:settings',     kind: 'page', title: 'Settings',          icon: <SettingsIcon fontSize="small" />,          run: () => navigate('/settings') },
  { id: 'p:guide',        kind: 'page', title: 'App Guide (new tab)', icon: <MenuBookIcon fontSize="small" />,        run: () => window.open('/app-guide', '_blank', 'noopener,noreferrer') },
];

const RECENT_KEY = 'cmdk.recent.v1';
const MAX_RECENT = 8;

const loadRecent = (): string[] => {
  try { const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
};
const pushRecent = (id: string) => {
  const list = loadRecent().filter(x => x !== id);
  list.unshift(id);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT))); } catch { /* ignore */ }
};

const CommandPalette: React.FC = () => {
  const navigate = useNavigate();
  const { openChartTab } = useTabs();
  const dispatch = useDispatch<AppDispatch>();
  const themeMode = useSelector((s: RootState) => s.auth.themeMode);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(0);
  const [watchlistSymbols, setWatchlistSymbols] = useState<{ symbol: string; exchange: string }[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useHotkey('mod+k', () => setOpen(v => !v), { preventDefault: true, allowInInputs: true });

  // Load watchlist symbols once when first opened
  useEffect(() => {
    if (!open || watchlistSymbols.length > 0) return;
    let cancelled = false;
    strategyScanService.listWatchlists().then(list => {
      if (cancelled) return;
      const merged = new Map<string, { symbol: string; exchange: string }>();
      for (const wl of list || []) {
        for (const s of wl.symbols || []) {
          const key = `${s.exchange}:${s.symbol}`.toUpperCase();
          if (!merged.has(key)) merged.set(key, s);
        }
      }
      setWatchlistSymbols(Array.from(merged.values()));
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [open, watchlistSymbols.length]);

  // Focus input on open + reset state on close
  useEffect(() => {
    if (open) {
      setQ('');
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const actionCommands: Command[] = useMemo(() => [
    {
      id: 'a:toggle-theme',
      kind: 'action',
      title: themeMode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
      icon: <Brightness4Icon fontSize="small" />,
      run: () => dispatch(setThemeMode(themeMode === 'dark' ? 'light' : 'dark')),
    },
    {
      id: 'a:show-hotkeys',
      kind: 'action',
      title: 'Show keyboard shortcuts',
      icon: <ArticleIcon fontSize="small" />,
      run: () => {
        // Trigger the HotkeyOverlay by synthesising a `?` keystroke
        window.dispatchEvent(new KeyboardEvent('keydown', { key: '?' }));
      },
    },
  ], [themeMode, dispatch]);

  const symbolCommands: Command[] = useMemo(() => watchlistSymbols.map(s => ({
    id: `s:${s.exchange}:${s.symbol}`,
    kind: 'symbol' as const,
    title: s.symbol,
    subtitle: s.exchange,
    exchange: s.exchange,
    run: () => openChartTab(s.symbol, s.exchange),
  })), [watchlistSymbols, openChartTab]);

  const allCommands = useMemo(() => {
    const pages = PAGE_COMMANDS(navigate);
    return [...pages, ...actionCommands, ...symbolCommands];
  }, [navigate, actionCommands, symbolCommands]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) {
      const recent = loadRecent();
      const recentSet = new Set(recent);
      const recentCmds = recent.map(id => allCommands.find(c => c.id === id)).filter(Boolean) as Command[];
      const rest = allCommands.filter(c => !recentSet.has(c.id));
      return [...recentCmds, ...rest].slice(0, 30);
    }
    const score = (c: Command) => {
      const t = c.title.toLowerCase();
      if (t === query) return 0;
      if (t.startsWith(query)) return 1;
      if (t.includes(query)) return 2;
      if ((c.subtitle || '').toLowerCase().includes(query)) return 3;
      return 99;
    };
    return allCommands
      .map(c => ({ c, s: score(c) }))
      .filter(x => x.s < 99)
      .sort((a, b) => a.s - b.s)
      .slice(0, 30)
      .map(x => x.c);
  }, [q, allCommands]);

  // Clamp selected when results change
  useEffect(() => {
    if (selected >= filtered.length) setSelected(0);
  }, [filtered.length, selected]);

  const runCommand = (cmd: Command) => {
    pushRecent(cmd.id);
    cmd.run();
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected(s => Math.min(s + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected(s => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[selected];
      if (cmd) runCommand(cmd);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { mt: 8, borderRadius: 2, overflow: 'hidden' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            inputRef={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            fullWidth
            placeholder="Jump to page, symbol, or run an action…"
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Chip size="small" label="ESC" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List sx={{ maxHeight: 380, overflowY: 'auto', p: 0 }}>
          {filtered.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">No matches</Typography>
            </Box>
          )}
          {filtered.map((cmd, i) => (
            <ListItemButton
              key={cmd.id}
              selected={i === selected}
              onMouseEnter={() => setSelected(i)}
              onClick={() => runCommand(cmd)}
              sx={{ gap: 1, py: 0.75 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: 24, color: 'text.secondary' }}>
                {cmd.icon}
              </Box>
              <ListItemText
                primary={cmd.title}
                secondary={cmd.subtitle}
                primaryTypographyProps={{ fontSize: '0.9rem' }}
                secondaryTypographyProps={{ fontSize: '0.72rem' }}
              />
              <Chip
                size="small"
                label={cmd.kind === 'page' ? 'Page' : cmd.kind === 'symbol' ? cmd.exchange || 'Symbol' : 'Action'}
                sx={{ fontSize: '0.66rem', height: 20 }}
              />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.5, fontSize: '0.7rem', color: 'text.secondary' }}>
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span>Esc close</span>
          <span style={{ marginLeft: 'auto' }}>Ctrl/⌘+K to toggle</span>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommandPalette;
